// src/pages/personal/analytics/MarketingExpensesSection/hooks/useMarketingExpensesData.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Period, DateRange } from '../../types/types';

// Типы для метрик маркетинговых расходов
export interface MarketingMetrics {
  totalSpend: Metric;  // Общие расходы
  impressions: Metric; // Показы
  clicks: Metric;      // Клики
  uniqueReach: Metric; // Уникальный охват
  ctr: Metric;         // CTR (Click-Through Rate)
}

export interface Metric {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

// Интерфейс точки данных для графика
export interface ExpensesDataPoint {
  name: string;
  displayName?: string;
  totalSpend?: number;
  impressions?: number;
  clicks?: number;
  uniqueReach?: number;
  ctr?: number;
  // Дополнительные поля для фильтрации и отображения
  day?: number;
  month?: number;
  year?: number;
  hour?: number;
  isSunday?: boolean;
}

// Интерфейс для распределения по каналам/платформам
export interface DistributionItem {
  name: string;
  value: number;
  percentage: number;
  color?: string;
  impressions?: number;
  clicks?: number;
}

// Интерфейс возвращаемого значения хука
interface UseMarketingExpensesDataReturn {
  metrics: MarketingMetrics | null;
  expensesData: ExpensesDataPoint[];
  channelDistribution: DistributionItem[];
  platformDistribution: DistributionItem[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

// Цвета для каналов привлечения
const CHANNEL_COLORS: Record<string, string> = {
  instagram: '#C13584', // Цвет Instagram
  facebook: '#4267B2',  // Цвет Facebook
  seo: '#34A853',       // Зеленый для SEO
  maps: '#4285F4',      // Синий для карт
  walk_in: '#FBBC05',   // Желтый для визитов в магазин
  referral: '#EA4335',  // Красный для рефералов
  other: '#757575',     // Серый для других источников
};

// Цвета для платформ
const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#C13584', // Цвет Instagram
  facebook: '#4267B2',  // Цвет Facebook
  google: '#34A853',    // Зеленый для Google
  '2gis': '#FBBC05',    // Желтый для 2ГИС
  other: '#757575',     // Серый для других источников
};

// Основной хук для получения данных о маркетинговых расходах
export const useMarketingExpensesData = (
  period: Period, 
  dateRange?: DateRange
): UseMarketingExpensesDataReturn => {
  const { t } = useTranslation(['analytics', 'errors']);
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  // Состояния для данных
  const [metrics, setMetrics] = useState<MarketingMetrics | null>(null);
  const [expensesData, setExpensesData] = useState<ExpensesDataPoint[]>([]);
  const [channelDistribution, setChannelDistribution] = useState<DistributionItem[]>([]);
  const [platformDistribution, setPlatformDistribution] = useState<DistributionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Рефы для отслеживания состояния
  const isMounted = useRef(true);
  const isFirstRender = useRef(true);
  
  // Функция для получения данных о маркетинговых расходах
  const fetchMarketingData = useCallback(async () => {
    if (isBusinessIdLoading || !businessId || !isMounted.current) {
      return;
    }
    
    // Сбрасываем флаг первого рендера
    isFirstRender.current = false;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Проверяем валидность dateRange
      const isDateRangeValid = dateRange && 
        dateRange.startDate instanceof Date && !isNaN(dateRange.startDate.getTime()) &&
        dateRange.endDate instanceof Date && !isNaN(dateRange.endDate.getTime());
      
      // Формируем параметры запроса
      const params: any = { 
        business: businessId.toString(),
        period: period
      };
      
      // Если есть диапазон дат и он валидный, добавляем его
      if (isDateRangeValid) {
        params.start_date = dateRange.startDate.toISOString();
        params.end_date = dateRange.endDate.toISOString();
      }
      
      // Получаем данные о маркетинговых расходах
      const response = await api.get('/api/analytics/marketing/expenses/stats/', params);
      
      if (!isMounted.current) {
        return;
      }
      
      if (response) {
        // Формируем новые метрики из данных API
        const totalSpend = response.metrics?.totalSpend || { 
          value: 0, 
          change: 0, 
          trend: 'neutral' as 'up' | 'down' | 'neutral'
        };
        
        // Получаем данные по показам и кликам из ответа API или создаем пустые метрики
        const impressionsValue = response.total_impressions || 0;
        const impressionsChange = response.impressions_change || 0;
        const impressionsTrend: 'up' | 'down' | 'neutral' = 
          impressionsChange > 0 ? 'up' : 
          impressionsChange < 0 ? 'down' : 
          'neutral';
        
        const impressions = {
          value: impressionsValue,
          change: impressionsChange,
          trend: impressionsTrend
        };
        
        const clicksValue = response.total_clicks || 0;
        const clicksChange = response.clicks_change || 0;
        const clicksTrend: 'up' | 'down' | 'neutral' = 
          clicksChange > 0 ? 'up' : 
          clicksChange < 0 ? 'down' : 
          'neutral';
        
        const clicks = {
          value: clicksValue,
          change: clicksChange,
          trend: clicksTrend
        };
        
        const uniqueReachValue = response.total_unique_reach || 0;
        const uniqueReachChange = response.unique_reach_change || 0;
        const uniqueReachTrend: 'up' | 'down' | 'neutral' = 
          uniqueReachChange > 0 ? 'up' : 
          uniqueReachChange < 0 ? 'down' : 
          'neutral';
        
        const uniqueReach = {
          value: uniqueReachValue,
          change: uniqueReachChange,
          trend: uniqueReachTrend
        };
        
        const ctrValue = response.ctr || 0;
        const ctrChange = response.ctr_change || 0;
        const ctrTrend: 'up' | 'down' | 'neutral' = 
          ctrChange > 0 ? 'up' : 
          ctrChange < 0 ? 'down' : 
          'neutral';
        
        const ctr = {
          value: ctrValue,
          change: ctrChange,
          trend: ctrTrend
        };
        
        // Обновляем метрики
        setMetrics({
          totalSpend,
          impressions,
          clicks,
          uniqueReach,
          ctr
        });
        
        // Обрабатываем данные для графика
        if (response.expenses_by_period) {
          setExpensesData(response.expenses_by_period);
        }
        
        // Обрабатываем распределение по каналам
        if (response.channel_distribution) {
          const channelData = response.channel_distribution.map((item: any) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
            color: CHANNEL_COLORS[item.name] || CHANNEL_COLORS.other,
            impressions: item.impressions || 0,
            clicks: item.clicks || 0
          }));
          setChannelDistribution(channelData);
        }
        
        // Обрабатываем распределение по платформам
        if (response.platform_distribution) {
          const platformData = response.platform_distribution.map((item: any) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
            color: PLATFORM_COLORS[item.name] || PLATFORM_COLORS.other,
            impressions: item.impressions || 0,
            clicks: item.clicks || 0
          }));
          setPlatformDistribution(platformData);
        }
      } else {
        setError(t('errors:marketing_data_loading_error'));
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error('Ошибка загрузки данных о маркетинговых расходах:', error);
        setError(error.message || t('errors:marketing_data_loading_error'));
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [businessId, isBusinessIdLoading, period, dateRange, t]);
  
  // Эффект для инициализации и обновления данных
  useEffect(() => {
    isMounted.current = true;
    
    // Запускаем первичную загрузку данных
    fetchMarketingData();
    
    return () => {
      isMounted.current = false;
    };
  }, [fetchMarketingData]);
  
  return {
    metrics,
    expensesData,
    channelDistribution,
    platformDistribution,
    isLoading,
    error,
    refreshData: fetchMarketingData
  };
};